"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import {
  Field,
  TextInput,
  TextArea,
  PrintButton,
  CopyButton,
  DataReviewedNote,
  PrintFooter,
  trackToolResult,
} from "@/components/tools/ui";
import { AE_DEPARTMENTS, DATASET_DATE, type AeDepartment } from "@/lib/aeDepartments";

/* Nearest A&E finder. Resolves a site postcode to coordinates via postcodes.io
   (free, CORS-enabled, no key), then ranks the Type 1 ED dataset by straight-line
   (haversine) distance. The output is an "Emergency arrangements" block the user
   can copy or print into a RAMS.

   SAFETY: this is NOT an emergency service and distances are straight-line only.
   The 999 message is shown prominently and on the printed output. */

interface Ranked extends AeDepartment {
  miles: number;
  mapsUrl: string;
}

const EARTH_MILES = 3958.8;

function haversineMiles(lat1: number, lng1: number, lat2: number, lng2: number): number {
  const toRad = (d: number) => (d * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) * Math.sin(dLng / 2);
  return EARTH_MILES * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

function mapsLink(d: AeDepartment): string {
  const dest = encodeURIComponent(`${d.hospital}, ${d.postcode}`);
  return `https://www.google.com/maps/dir/?api=1&destination=${dest}`;
}

function fmtMiles(m: number): string {
  return m < 10 ? m.toFixed(1) : Math.round(m).toString();
}

type Status = "idle" | "loading" | "ok" | "notfound" | "error";

/** Loose full-UK-postcode shape (outward + inward code) — format guidance only,
    existence is checked by the postcodes.io lookup. */
const POSTCODE_SHAPE = /^[A-Z]{1,2}\d[A-Z\d]?\s*\d[A-Z]{2}$/i;

export function NearestAeFinder() {
  const [postcode, setPostcode] = useState("");
  const [status, setStatus] = useState<Status>("idle");
  const [origin, setOrigin] = useState<{ lat: number; lng: number; postcode: string } | null>(null);

  // Editable site / first-aid fields the user fills before printing.
  const [siteAddress, setSiteAddress] = useState("");
  const [firstAider, setFirstAider] = useState("");
  const [kitLocation, setKitLocation] = useState("");
  const [assemblyPoint, setAssemblyPoint] = useState("");
  // Access notes for emergency responders.
  const [siteEntrance, setSiteEntrance] = useState("");
  const [accessRestrictions, setAccessRestrictions] = useState("");
  const [emergencyContact, setEmergencyContact] = useState("");
  const [what3words, setWhat3words] = useState("");

  const resultTracked = useRef(false);

  const nearest = useMemo<Ranked[]>(() => {
    if (!origin) return [];
    return AE_DEPARTMENTS.map((d) => ({
      ...d,
      miles: haversineMiles(origin.lat, origin.lng, d.lat, d.lng),
      mapsUrl: mapsLink(d),
    }))
      .sort((a, b) => a.miles - b.miles)
      .slice(0, 3);
  }, [origin]);

  // Fire the funnel event once per visit when a result first appears.
  useEffect(() => {
    if (status === "ok" && nearest.length > 0 && !resultTracked.current) {
      resultTracked.current = true;
      trackToolResult("nearest-ae-finder", { found: true });
    }
  }, [status, nearest.length]);

  async function lookup(e: React.FormEvent) {
    e.preventDefault();
    const pc = postcode.trim();
    if (!pc) return;
    setStatus("loading");
    try {
      const res = await fetch(
        `https://api.postcodes.io/postcodes/${encodeURIComponent(pc)}`,
      );
      if (res.status === 404) {
        setOrigin(null);
        setStatus("notfound");
        return;
      }
      if (!res.ok) {
        setOrigin(null);
        setStatus("error");
        return;
      }
      const data = await res.json();
      const r = data.result;
      if (!r || typeof r.latitude !== "number" || typeof r.longitude !== "number") {
        setOrigin(null);
        setStatus("notfound");
        return;
      }
      setOrigin({ lat: r.latitude, lng: r.longitude, postcode: r.postcode });
      setStatus("ok");
    } catch {
      setOrigin(null);
      setStatus("error");
    }
  }

  function buildText(): string {
    const top = nearest[0];
    const lines: string[] = [];
    lines.push("EMERGENCY ARRANGEMENTS");
    lines.push("");
    lines.push("In an emergency, call 999.");
    lines.push("");
    if (siteAddress.trim()) {
      lines.push("Site address:");
      siteAddress.trim().split("\n").forEach((l) => lines.push(`  ${l}`));
      lines.push("");
    }
    if (top) {
      lines.push("Nearest A&E (24-hour emergency department):");
      lines.push(`  ${top.hospital}`);
      lines.push(`  ${top.town}, ${top.postcode}`);
      lines.push(`  Approx. ${fmtMiles(top.miles)} miles (straight-line) from site`);
      lines.push(`  Directions: ${top.mapsUrl}`);
      lines.push("");
      if (nearest.length > 1) {
        lines.push("Other nearby A&E departments:");
        nearest.slice(1).forEach((d) => {
          lines.push(`  ${d.hospital}, ${d.town}, ${d.postcode} (approx. ${fmtMiles(d.miles)} miles)`);
        });
        lines.push("");
      }
    }
    lines.push("First-aid arrangements:");
    lines.push(`  First aider: ${firstAider.trim() || "________________________"}`);
    lines.push(`  First-aid kit location: ${kitLocation.trim() || "________________________"}`);
    lines.push(`  Assembly point: ${assemblyPoint.trim() || "________________________"}`);
    lines.push("");
    if (
      siteEntrance.trim() ||
      accessRestrictions.trim() ||
      emergencyContact.trim() ||
      what3words.trim()
    ) {
      lines.push("Access for emergency responders:");
      if (siteEntrance.trim()) lines.push(`  Site gate / entrance: ${siteEntrance.trim()}`);
      if (accessRestrictions.trim()) lines.push(`  Access restrictions: ${accessRestrictions.trim()}`);
      if (emergencyContact.trim()) lines.push(`  Emergency contact: ${emergencyContact.trim()}`);
      if (what3words.trim()) lines.push(`  what3words: ${what3words.trim()}`);
      lines.push("");
    }
    lines.push(
      "Note: distances are straight-line. Confirm the driving route and that the department is open before work starts. In an emergency call 999 — do not drive a seriously injured person to A&E.",
    );
    lines.push(`Hospital list compiled ${DATASET_DATE}.`);
    return lines.join("\n");
  }

  const top = nearest[0];

  return (
    <div className="grid gap-6 lg:grid-cols-[380px_1fr]">
      {/* Inputs */}
      <div className="space-y-4">
        {/* Mandatory safety framing — visible before anything else. */}
        <div className="rounded-xl border border-risk-veryhigh/40 bg-[#fdecec] px-5 py-4 text-[#a31616]">
          <div className="font-display text-lg font-bold">In an emergency, always call 999</div>
          <p className="mt-1 text-[0.9rem] leading-relaxed">
            Do not drive a seriously injured person to A&amp;E based on this tool. Distances are
            straight-line; check the route before work starts and confirm the department is open —
            services change.
          </p>
        </div>

        <form onSubmit={lookup} className="card shadow-soft p-6">
          <h2 className="text-xl">Site postcode</h2>
          <p className="mt-1 text-sm text-muted">
            Enter the postcode of the work site. We look up its location to find the nearest 24-hour
            A&amp;E departments.
          </p>
          <div className="mt-4">
            <Field label="Postcode" hint="The full UK postcode of the site, e.g. SW1A 1AA — a partial postcode (SW1A) isn't enough to locate it.">
              <TextInput
                value={postcode}
                onChange={(e) => setPostcode(e.target.value)}
                placeholder="e.g. SW1A 1AA"
                autoComplete="postal-code"
                spellCheck={false}
                aria-describedby="postcode-help"
              />
            </Field>
            {/* Live format guidance: explain what the disabled button is waiting for. */}
            <p id="postcode-help" aria-live="polite" className="mt-1.5 min-h-[1.1rem] text-[0.8rem]">
              {postcode.trim() && !POSTCODE_SHAPE.test(postcode.trim()) ? (
                <span className="font-semibold text-[#8a6400]">
                  Keep typing — that doesn&apos;t look like a full postcode yet (format: SW1A 1AA).
                </span>
              ) : postcode.trim() ? (
                <span className="font-semibold text-emerald-deep">Looks good — find the nearest A&amp;E below.</span>
              ) : null}
            </p>
          </div>
          <button
            type="submit"
            disabled={status === "loading" || !POSTCODE_SHAPE.test(postcode.trim())}
            className="mt-3 inline-flex w-full items-center justify-center gap-2 rounded-full bg-emerald px-6 py-3 text-[0.95rem] font-semibold text-white transition-all hover:bg-emerald-deep disabled:cursor-not-allowed disabled:opacity-60"
          >
            {status === "loading" ? "Finding…" : "Find nearest A&E"}
          </button>

          {status === "notfound" && (
            <p className="mt-3 text-[0.85rem] font-semibold text-risk-veryhigh">
              That postcode wasn&apos;t recognised. Check it and try again — use the full postcode
              (e.g. SW1A 1AA).
            </p>
          )}
          {status === "error" && (
            <p className="mt-3 text-[0.85rem] font-semibold text-risk-veryhigh">
              Couldn&apos;t reach the postcode lookup service. Check your connection and try again.
            </p>
          )}
          <p className="mt-3 text-[0.78rem] text-muted">
            Postcode lookup by postcodes.io (Ordnance Survey / ONS open data). Nothing is sent to us or stored by us.
          </p>
        </form>

        {status === "ok" && top && (
          <div className="card shadow-soft p-6">
            <h2 className="text-xl">First-aid details</h2>
            <p className="mt-1 text-sm text-muted">
              Fill these in to complete the emergency block (optional — leave blank to print fill-in
              lines).
            </p>
            <div className="mt-4 space-y-3">
              <Field label="Site address">
                <TextArea
                  value={siteAddress}
                  onChange={(e) => setSiteAddress(e.target.value)}
                  placeholder="Site name / street&#10;Town&#10;Postcode"
                />
              </Field>
              <Field label="Appointed first aider">
                <TextInput
                  value={firstAider}
                  onChange={(e) => setFirstAider(e.target.value)}
                  placeholder="Name / contact number"
                />
              </Field>
              <Field label="First-aid kit location">
                <TextInput
                  value={kitLocation}
                  onChange={(e) => setKitLocation(e.target.value)}
                  placeholder="e.g. site cabin / welfare unit"
                />
              </Field>
              <Field label="Assembly point">
                <TextInput
                  value={assemblyPoint}
                  onChange={(e) => setAssemblyPoint(e.target.value)}
                  placeholder="e.g. main gate car park"
                />
              </Field>

              <div className="border-t border-line pt-3">
                <div className="text-[0.78rem] font-semibold uppercase tracking-wide text-muted">
                  Access for emergency responders
                </div>
                <p className="mt-0.5 text-[0.8rem] text-muted">
                  Optional — helps an ambulance reach the casualty quickly.
                </p>
              </div>
              <Field label="Site gate / entrance">
                <TextInput
                  value={siteEntrance}
                  onChange={(e) => setSiteEntrance(e.target.value)}
                  placeholder="e.g. Compound gate off Mill Lane"
                />
              </Field>
              <Field label="Access restrictions">
                <TextInput
                  value={accessRestrictions}
                  onChange={(e) => setAccessRestrictions(e.target.value)}
                  placeholder="e.g. height barrier 2.1 m, keypad 1234"
                />
              </Field>
              <Field label="Emergency contact number">
                <TextInput
                  value={emergencyContact}
                  onChange={(e) => setEmergencyContact(e.target.value)}
                  placeholder="e.g. Site manager 07700 900000"
                  inputMode="tel"
                />
              </Field>
              <Field label="what3words" hint="Three-word location for the work area (what3words.com).">
                <TextInput
                  value={what3words}
                  onChange={(e) => setWhat3words(e.target.value)}
                  placeholder="e.g. ///filled.count.soap"
                />
              </Field>
            </div>
          </div>
        )}
      </div>

      {/* Output: emergency arrangements block */}
      <div className="space-y-4">
        {status === "ok" && top ? (
          <>
            <div className="print-area card shadow-soft p-6" id="ae-record">
              <div className="text-[0.74rem] font-semibold uppercase tracking-wide text-emerald-deep">
                Emergency arrangements
              </div>

              {/* 999 banner — repeated on the printable output. */}
              <div className="mt-2 rounded-xl border border-risk-veryhigh/40 bg-[#fdecec] px-5 py-3 text-[#a31616]">
                <span className="font-display text-lg font-bold">In an emergency, call 999.</span>{" "}
                <span className="text-[0.9rem]">
                  Do not drive a seriously injured person to A&amp;E — call an ambulance.
                </span>
              </div>

              {siteAddress.trim() && (
                <div className="mt-4">
                  <div className="text-[0.78rem] font-semibold uppercase tracking-wide text-muted">
                    Site address
                  </div>
                  <p className="mt-1 whitespace-pre-line text-[0.95rem] text-ink">{siteAddress.trim()}</p>
                </div>
              )}

              <div className="mt-4 flex items-start justify-between gap-4">
                <div>
                  <div className="text-[0.78rem] font-semibold uppercase tracking-wide text-muted">
                    Nearest A&amp;E (24-hour emergency department)
                  </div>
                  <p className="mt-1 font-display text-lg font-bold text-ink">{top.hospital}</p>
                  <p className="text-[0.95rem] text-body">
                    {top.town}, {top.postcode}
                  </p>
                  <p className="mt-1 text-[0.9rem] text-muted">
                    Approx. <strong className="text-ink">{fmtMiles(top.miles)} miles</strong>{" "}
                    straight-line from {origin?.postcode}
                    {top.note ? ` — ${top.note}` : ""}
                  </p>
                  <a
                    href={top.mapsUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-2 inline-flex items-center gap-1.5 text-[0.9rem] font-semibold text-emerald-deep underline print-hide"
                  >
                    Directions on Google Maps
                    <svg viewBox="0 0 24 24" className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M7 17 17 7" /><path d="M7 7h10v10" />
                    </svg>
                  </a>
                </div>
                {/* QR of the directions link — scannable from the printed RAMS
                    pinned on the site wall. Destination-only; no user data. */}
                <div className="shrink-0 text-center">
                  {/* eslint-disable-next-line @next/next/no-img-element */}
                  <img
                    src={`https://api.qrserver.com/v1/create-qr-code/?size=176x176&data=${encodeURIComponent(top.mapsUrl)}`}
                    alt={`QR code — scan for directions to ${top.hospital}`}
                    width={88}
                    height={88}
                    className="h-[88px] w-[88px]"
                  />
                  <div className="mt-0.5 text-[0.62rem] uppercase tracking-wide text-muted">Scan for directions</div>
                </div>
              </div>

              {nearest.length > 1 && (
                <div className="mt-4">
                  <div className="text-[0.78rem] font-semibold uppercase tracking-wide text-muted">
                    Other nearby A&amp;E departments
                  </div>
                  <ul className="mt-1.5 space-y-1.5">
                    {nearest.slice(1).map((d) => (
                      <li key={d.postcode} className="text-[0.9rem] text-body">
                        <span className="text-ink">{d.hospital}</span>, {d.town}, {d.postcode}{" "}
                        <span className="text-muted">(approx. {fmtMiles(d.miles)} miles{d.note ? `, ${d.note}` : ""})</span>{" "}
                        <a
                          href={d.mapsUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="font-semibold text-emerald-deep underline print-hide"
                        >
                          directions
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="mt-4">
                <div className="text-[0.78rem] font-semibold uppercase tracking-wide text-muted">
                  First-aid arrangements
                </div>
                <dl className="mt-1.5 space-y-1 text-[0.92rem]">
                  <div className="flex gap-2">
                    <dt className="text-muted">First aider:</dt>
                    <dd className="text-ink">{firstAider.trim() || "________________________"}</dd>
                  </div>
                  <div className="flex gap-2">
                    <dt className="text-muted">First-aid kit location:</dt>
                    <dd className="text-ink">{kitLocation.trim() || "________________________"}</dd>
                  </div>
                  <div className="flex gap-2">
                    <dt className="text-muted">Assembly point:</dt>
                    <dd className="text-ink">{assemblyPoint.trim() || "________________________"}</dd>
                  </div>
                </dl>
              </div>

              {(siteEntrance.trim() || accessRestrictions.trim() || emergencyContact.trim() || what3words.trim()) && (
                <div className="mt-4">
                  <div className="text-[0.78rem] font-semibold uppercase tracking-wide text-muted">
                    Access for emergency responders
                  </div>
                  <dl className="mt-1.5 space-y-1 text-[0.92rem]">
                    {siteEntrance.trim() && (
                      <div className="flex gap-2">
                        <dt className="text-muted">Site gate / entrance:</dt>
                        <dd className="text-ink">{siteEntrance.trim()}</dd>
                      </div>
                    )}
                    {accessRestrictions.trim() && (
                      <div className="flex gap-2">
                        <dt className="text-muted">Access restrictions:</dt>
                        <dd className="text-ink">{accessRestrictions.trim()}</dd>
                      </div>
                    )}
                    {emergencyContact.trim() && (
                      <div className="flex gap-2">
                        <dt className="text-muted">Emergency contact:</dt>
                        <dd className="text-ink">{emergencyContact.trim()}</dd>
                      </div>
                    )}
                    {what3words.trim() && (
                      <div className="flex gap-2">
                        <dt className="text-muted">what3words:</dt>
                        <dd className="text-ink">{what3words.trim()}</dd>
                      </div>
                    )}
                  </dl>
                </div>
              )}

              <p className="mt-4 rounded-lg bg-mist px-3 py-2 text-[0.8rem] leading-relaxed text-muted">
                Distances are straight-line, not driving distance. Confirm the route and that the
                department is open before work starts.
              </p>
              <DataReviewedNote date={DATASET_DATE}>
                A&amp;E provision changes — departments are downgraded or moved to booked models. Verify
                the department is open before relying on it.
              </DataReviewedNote>

              <PrintFooter toolName="nearest A&E finder" />
            </div>

            <div className="flex flex-wrap justify-end gap-3 print-hide">
              <CopyButton tool="nearest-ae-finder" buildText={buildText} />
              <PrintButton tool="nearest-ae-finder" label="Print / save block as PDF" />
            </div>

            <p className="text-[0.8rem] leading-relaxed text-muted print-hide">
              If the postcode lookup fails or you want to double-check, search the{" "}
              <a
                href="https://www.nhs.uk/service-search/accident-and-emergency-services/"
                target="_blank"
                rel="noopener noreferrer"
                className="font-semibold text-emerald-deep underline"
              >
                NHS A&amp;E service finder
              </a>
              .
            </p>
          </>
        ) : (
          <div className="card border-dashed p-6 text-center text-sm text-muted">
            Enter your site postcode to find the three nearest 24-hour A&amp;E departments and build a
            ready-to-paste emergency arrangements block for your RAMS.
          </div>
        )}
      </div>
    </div>
  );
}
